<template>
  <LoadingIndicator v-if="loading" message="Loading template..." />
  <div v-else-if="loadError" class="rounded-md bg-red-tint px-4 py-3 text-gray-900">{{ loadError }}</div>
  <div v-else class="space-y-6">
    <HermesRecordHeader :back="{ name: 'AdminHermesTemplates' }" back-label="All templates" :title="isNew ? 'New template' : saved.name" :meta="isNew ? '' : `#${saved.id} · version ${saved.version}`">
    </HermesRecordHeader>

    <div class="flex gap-6 items-start">
      <!-- Edit with AI takes the full width; its own bar links back to the template. -->
      <div v-if="section !== 'drafts'" class="sticky top-4 shrink-0 rounded-lg border border-gray-200 overflow-hidden">
        <PageSidebar title="" width="w-64" :show-collapse-icon="false" :items="sectionItems" @item-click="openSection($event.id)" />
      </div>

      <div class="flex-1 min-w-0">
        <button v-if="section === 'drafts'" type="button" :class="[TEXT_BUTTON, 'mb-3']" data-test="back-to-template" @click="openSection('overview')">← Back to the template's settings</button>
        <!-- Two ways to change a document template: by hand here (live, straight away), or safely in a draft with AI. -->
        <div v-if="!isNew && hasDrafts && section !== 'drafts'" class="mb-4 flex flex-wrap items-center gap-4 rounded-lg border border-primary-200 bg-primary-50 px-4 py-3" data-test="edit-with-ai-banner">
          <div class="min-w-0 flex-1">
            <div class="font-medium text-gray-900">
              You're looking at the live template.
              <span v-if="saved.is_active === false" class="ml-2 rounded bg-neutral-100 px-2 font-normal text-gray-900" data-test="live-inactive">Inactive</span>
            </div>
            <p v-if="saved.is_active !== false" class="mt-1 text-gray-700">Changes you save here reach users straight away. To try changes first, or have AI make them, use Edit with AI.</p>
            <p v-else class="mt-1 text-gray-700" data-test="live-inactive-note">It's inactive, so nothing runs it and users don't get it, even after you save or publish. Make it active under Overview when it's ready. You can still try drafts on an engagement.</p>
          </div>
          <button type="button" :class="PRIMARY_BUTTON" data-test="go-edit-with-ai" @click="openSection('drafts')">Edit with AI ✨</button>
        </div>
        <HermesCard v-if="section === 'overview'" title="Overview">
          <div class="space-y-4">
            <label class="block"><span class="block text-gray-500 mb-2">Name</span><input v-model="form.name" :class="INPUT"></label>
            <label class="block"><span class="block text-gray-500 mb-2">Description</span><textarea v-model="form.description" rows="2" :class="INPUT" /></label>
            <div class="flex items-center gap-6 border-t border-gray-200 pt-4">
              <div>
                <div class="font-medium text-gray-900">Active</div>
                <div class="text-gray-500">Runs and schedules can use this template. When it's off, no new runs start.</div>
              </div>
              <Toggle v-model="form.is_active" aria-label="Active" />
            </div>
            <div class="flex items-center gap-6 border-t border-gray-200 pt-4" data-test="kind-toggle">
              <div>
                <div class="font-medium text-gray-900">Engagement summary</div>
                <div class="text-gray-500">Writes one care management document onto an engagement, from Summarize or a program button. It needs the summary_documents effect, a work type in Display, and the unit WORK_ID.</div>
              </div>
              <Toggle :model-value="form.kind === ENGAGEMENT_SUMMARY_KIND" aria-label="Engagement summary" @update:model-value="form.kind = $event ? ENGAGEMENT_SUMMARY_KIND : ''" />
            </div>
            <div class="flex items-center gap-6 border-t border-gray-200 pt-4" data-test="team-review-toggle">
              <div>
                <div class="font-medium text-gray-900">Team review</div>
                <div class="text-gray-500">Shows one result per patient on the Team Review board (Care Management, Utilization Mgmt). It needs the unit BENE_MBI_ID, and idt_board and display_schema in Display.</div>
              </div>
              <Toggle :model-value="form.kind === TEAM_REVIEW_KIND" aria-label="Team review" @update:model-value="form.kind = $event ? TEAM_REVIEW_KIND : ''" />
            </div>
            <div class="flex items-center gap-6 border-t border-gray-200 pt-4" data-test="qa-rubric-toggle">
              <div>
                <div class="font-medium text-gray-900">QA rubric</div>
                <div class="text-gray-500">An audit rubric in Care Management, QA: managers answer its questions Yes, No or N/A for one call. Everything it needs is qa in Display (questions, thresholds and the task types whose calls it audits). Never change a question's id once audits use it.</div>
              </div>
              <Toggle :model-value="form.kind === QA_RUBRIC_KIND" aria-label="QA rubric" @update:model-value="form.kind = $event ? QA_RUBRIC_KIND : ''" />
            </div>
            <label class="block border-t border-gray-200 pt-4">
              <span class="block text-gray-500 mb-2">Executor</span>
              <FilterDropdown v-model="form.executor" class="w-full" full-width :options="executorOptions" placeholder="From the schedule, else the default" search-placeholder="Search executors" searchable aria-label="Executor" clearable />
              <span class="block text-gray-500 mt-2">Runs this template when an event starts it, such as Summarize on an engagement. Empty uses the schedule's executor.</span>
            </label>
            <div v-if="showsPrograms" class="block" data-test="programs-field">
              <span class="block text-gray-500 mb-2">{{ programNoun }}</span>
              <MultiSelectDropdown v-model="form.metrics" :options="metricOptions" :sensitive-labels="false" :placeholder="`Choose ${programNoun.toLowerCase()}`" searchable />
              <span v-if="isQaRubricForm" class="block text-gray-500 mt-2" data-test="qa-programs-help">Audits calls on these {{ programNoun.toLowerCase() }}' task types, as well as any task types listed in Display under qa.task_type_ids.</span>
              <span v-else class="block text-gray-500 mt-2">Only for engagements in these {{ programNoun.toLowerCase() }}. An active document needs at least one.</span>
            </div>
            <div v-if="showsPrograms && !isQaRubricForm" class="block" data-test="engagement-types-field">
              <span class="block text-gray-500 mb-2">Engagement types</span>
              <MultiSelectDropdown v-model="form.engagement_types" :options="engagementTypeOptions" :sensitive-labels="false" placeholder="Choose engagement types" searchable />
              <span class="block text-gray-500 mt-2">Only for these kinds of engagement; an active document needs at least one. Summarize generates this document when both the engagement's {{ programNounSingular.toLowerCase() }} and its engagement type are listed.</span>
            </div>
            <!-- This page edits the template; running it lives on the Runs page. -->
            <div v-if="!isNew" class="flex items-center gap-4 border-t border-gray-200 pt-4" data-test="template-runs">
              <div class="flex-1">
                <div class="font-medium text-gray-900">Runs</div>
                <div class="text-gray-500">Every time this template has run, and starting one by hand, are on the Runs page.</div>
              </div>
              <router-link :to="{ name: 'AdminHermesRuns', query: { template: saved.id } }" :class="TEXT_BUTTON">View runs</router-link>
              <router-link :to="{ name: 'AdminHermesRuns', query: { start: saved.id } }" :class="SECONDARY_BUTTON">Start run…</router-link>
            </div>
          </div>
        </HermesCard>

        <HermesCard v-else-if="section === 'population'" title="Population">
          <div class="space-y-6">
            <fieldset>
              <legend class="text-gray-500 mb-2">Run type</legend>
              <div class="space-y-2">
                <label :class="['flex gap-3 rounded-md border p-3 cursor-pointer', form.population ? 'border-primary-500 bg-primary-50' : 'border-gray-200']">
                  <input type="radio" name="population-source" :checked="!!form.population" class="mt-1" @change="form.population = { kind: 'data_attribute', ref: null }">
                  <span class="flex-1">
                    <span class="block font-medium text-gray-900">Batch over a DataAttribute</span>
                    <span class="block text-gray-600">Runs on every row the DataAttribute returns, with one result per row. Can be scheduled.</span>
                    <FilterDropdown v-if="form.population" v-model="form.population.ref" class="mt-3 w-full" full-width :options="dataAttributeOptions" placeholder="Choose a DataAttribute" search-placeholder="Search by name or #id" searchable aria-label="Batch DataAttribute" clearable />
                    <a v-if="form.population && form.population.ref" :href="daHref(form.population.ref)" target="_blank" rel="noopener" class="mt-2 inline-flex items-center gap-1.5 font-medium text-neutral-800 hover:underline">Open in Metric Studio <font-awesome-icon :icon="['fas', 'arrow-up-right-from-square']" class="text-xs" /></a>
                  </span>
                </label>
                <label v-tooltip="singleBlocked ? singleBlockedReason : ''" :class="['flex gap-3 rounded-md border p-3', !form.population ? 'border-primary-500 bg-primary-50' : 'border-gray-200', singleBlocked ? 'cursor-not-allowed' : 'cursor-pointer']">
                  <input type="radio" name="population-source" :checked="!form.population" :disabled="singleBlocked" class="mt-1" @change="form.population = null">
                  <span :class="singleBlocked ? 'opacity-60' : ''">
                    <span class="block font-medium text-gray-900">Single run</span>
                    <span class="block text-gray-600">Runs once each time a person or an event starts it. Can't be scheduled.</span>
                    <span v-if="singleBlocked" class="sr-only">{{ singleBlockedReason }}</span>
                    <a v-if="singleBlocked" :href="schedulesHref" target="_blank" rel="noopener" class="mt-2 inline-flex items-center gap-1.5 font-medium text-neutral-800 hover:underline">
                      View the {{ schedules.length }} schedule{{ schedules.length === 1 ? '' : 's' }}
                      <font-awesome-icon :icon="['fas', 'arrow-up-right-from-square']" class="text-xs" />
                    </a>
                  </span>
                </label>
              </div>
            </fieldset>
            <label class="block max-w-md">
              <span class="block text-gray-500 mb-2">Unit column</span>
              <input v-model="form.unit" :class="INPUT" placeholder="BENE_MBI_ID">
              <span class="block text-gray-500 mt-2">The key column one result is about.</span>
            </label>
          </div>
        </HermesCard>

        <HermesCard v-else-if="section === 'inputs'" title="Inputs" :meta="`${form.inputs.length} sources, in the order the model reads them`">
          <div class="space-y-4">
            <div v-for="(item, i) in form.inputs" :key="i" class="rounded-md border border-gray-200 p-4">
              <div class="flex items-center gap-3 mb-3">
                <span class="text-gray-500 tabular-nums">{{ i + 1 }}</span>
                <span class="rounded bg-neutral-100 px-2 text-gray-900">{{ words(item.kind) }}</span>
                {{ item.label }}
                <div class="ml-auto flex gap-3">
                  <button type="button" :class="TEXT_BUTTON" :disabled="i === 0" @click="move(i, -1)">Move up</button>
                  <button type="button" :class="TEXT_BUTTON" :disabled="i === form.inputs.length - 1" @click="move(i, 1)">Move down</button>
                  <button type="button" :class="TEXT_BUTTON" @click="form.inputs.splice(i, 1)">Remove</button>
                </div>
              </div>
              <div class="grid grid-cols-12 gap-4">
                <label class="col-span-4"><span class="block text-gray-500 mb-2">Label</span><input v-model="item.label" :class="INPUT"></label>
                <label class="col-span-8"><span class="block text-gray-500 mb-2">Description</span><input v-model="item.description" :class="INPUT"></label>
                <template v-if="item.kind === 'data_attribute'">
                  <div class="col-span-6">
                    <span class="block text-gray-500 mb-2">DataAttribute</span>
                    <FilterDropdown v-model="item.ref" class="w-full" full-width :options="dataAttributeOptions" placeholder="Choose a DataAttribute" search-placeholder="Search by name or #id" searchable aria-label="DataAttribute" clearable />
                    <a v-if="item.ref" :href="daHref(item.ref)" target="_blank" rel="noopener" class="mt-2 inline-flex items-center gap-1.5 font-medium text-neutral-800 hover:underline">Open in Metric Studio <font-awesome-icon :icon="['fas', 'arrow-up-right-from-square']" class="text-xs" /></a>
                    <span class="block text-gray-500 mt-2">Narrowed to each unit on {{ form.unit || 'the unit column' }}.</span>
                  </div>
                  <div class="col-span-6">
                    <span class="block text-gray-500 mb-2">Filters (fixed value, or {param} to bind a run parameter)</span>
                    <div v-for="(f, j) in filterRows(item)" :key="j" class="flex gap-2 mb-2">
                      <input :value="f.name" :class="INPUT" placeholder="name" @input="setFilter(item, j, 'name', $event.target.value)">
                      <input :value="f.value" :class="[INPUT, '', String(f.value).startsWith('{') ? 'border-dashed border-primary-500' : '']" placeholder="value or {param}" @input="setFilter(item, j, 'value', $event.target.value)">
                      <button type="button" :class="TEXT_BUTTON" @click="removeFilter(item, j)">Remove</button>
                    </div>
                    <button type="button" :class="TEXT_BUTTON" @click="addFilter(item)">Add filter</button>
                  </div>
                  <label class="col-span-6">
                    <span class="block text-gray-500 mb-2">Render as</span>
                    <select :value="item.render || ''" :class="INPUT" @change="setRender(item, $event.target.value)">
                      <option value="">Rows (column=value lines)</option>
                      <option v-for="r in catalog.renderers || []" :key="r" :value="r">{{ words(r) }}</option>
                    </select>
                    <span class="block text-gray-500 mt-2">A named renderer expects the DataAttribute's columns in the shape it was written for.</span>
                  </label>
                </template>
                <template v-else-if="item.kind === 'patient_graph'">
                  <div class="col-span-12">
                    <label class="inline-flex items-center gap-2 mb-3"><input v-model="item.required" type="checkbox" class="rounded border-gray-300"> Required; a unit with no graph fails instead of running without it</label>
                    <div class="grid grid-cols-12 gap-2">
                      <label v-for="n in catalog.graph_nodes" :key="n.key" class="col-span-4 inline-flex items-center gap-2">
                        <input type="checkbox" :checked="(item.nodes || catalog.default_graph_nodes).includes(n.key)" @change="toggleNode(item, n.key)"> {{ n.label }}
                      </label>
                    </div>
                  </div>
                </template>
                <template v-else-if="item.kind === 'case'">
                  <label class="col-span-8">
                    <span class="block text-gray-500 mb-2">User turns, one per line, played in order</span>
                    <textarea :value="(item.turns || []).join('\n')" :class="[INPUT, 'font-mono h-24']" @input="item.turns = lines($event.target.value)" />
                  </label>
                  <label class="col-span-4">
                    <span class="block text-gray-500 mb-2">Answers if the agent asks, one per line</span>
                    <textarea :value="(item.answers || []).join('\n')" :class="[INPUT, 'font-mono h-24']" @input="item.answers = lines($event.target.value)" />
                  </label>
                  <label class="col-span-4">
                    <span class="block text-gray-500 mb-2">Required hat path (optional, comma-separated)</span>
                    <input v-model="item.hats" :class="INPUT" placeholder="population,practice_performance,reporter">
                  </label>
                  <label class="col-span-8">
                    <span class="block text-gray-500 mb-2">Rubric for the judge</span>
                    <input v-model="item.rubric" :class="INPUT" placeholder="A good answer uses the attributed panel and states the year and contract it used.">
                  </label>
                  <label class="col-span-12">
                    <span class="block text-gray-500 mb-2">Expectations (JSON: tools_any, tools_never, tables_any, tables_never, hats, max_tool_calls, writes, mentions)</span>
                    <textarea :value="expectText(item)" :class="[INPUT, 'font-mono h-20']" @change="setExpect(item, $event.target.value)" />
                    <span v-if="item._expectError" class="text-red-600">{{ item._expectError }}</span>
                  </label>
                </template>
                <label v-else class="col-span-6">
                  <span class="block text-gray-500 mb-2">Source</span>
                  <select v-model="item.ref" :class="INPUT">
                    <option v-for="s in catalog.sources" :key="s.name" :value="s.name">{{ s.name }}</option>
                  </select>
                </label>
              </div>
            </div>
            <div class="flex gap-3">
              <span class="text-gray-600">Add input:</span>
              <button v-for="k in ['data_attribute', 'patient_graph', 'source', 'case']" :key="k" type="button" :class="TEXT_BUTTON" @click="addInput(k)">{{ words(k) }}</button>
              <button type="button" :class="TEXT_BUTTON" @click="showCasePaste = !showCasePaste">Paste cases as JSON</button>
            </div>
            <div v-if="showCasePaste" class="rounded-md border border-gray-200 p-4">
              <span class="block text-gray-500 mb-2">A JSON list of cases: {label, turns, answers?, hats?, expect?, rubric?}. Replaces the existing case inputs.</span>
              <textarea v-model="casePaste" :class="[INPUT, 'font-mono h-40']" />
              <div class="flex gap-3 mt-2">
                <button type="button" :class="TEXT_BUTTON" @click="importCases">Replace cases</button>
                <span v-if="casePasteError" class="text-red-600">{{ casePasteError }}</span>
              </div>
            </div>
          </div>
        </HermesCard>

        <HermesCard v-else-if="section === 'prompt'" title="Prompt">
          <template v-if="isSuperuser" #actions>
            <div class="inline-flex rounded-md border border-gray-300 overflow-hidden">
              <button v-for="v in ['write', 'preview']" :key="v" type="button" :class="['px-3 py-1', promptView === v ? 'bg-neutral-100 text-gray-900' : 'bg-white text-gray-600']" @click="promptView = v">{{ v === 'write' ? 'Write' : 'Preview' }}</button>
            </div>
          </template>
          <template v-if="isSuperuser && promptView === 'preview'">
            <p class="text-gray-500 mb-2">How this reads as markdown. The model receives the plain text exactly as written.<span class="ml-2 rounded bg-white border border-gray-200 px-2 text-[11px] font-normal text-gray-500">Beta · superusers only</span></p>
            <div class="prose prose-sm max-w-none rounded-md border border-gray-200 px-4 py-3" v-html="promptHtml" />
          </template>
          <template v-else>
            <p class="text-gray-500 mb-2">Write {name} where a run parameter goes. Wrap a clause in {param_start:name} … {param_end:name} to drop it when the run omits the value.</p>
            <textarea ref="prompt" v-model="form.prompt" rows="18" :class="[INPUT, 'font-mono leading-5']" spellcheck="false" />
          </template>
          <div v-if="isSuperuser && promptNotes.length" class="mt-3 rounded-md bg-neutral-50 px-3 py-2">
            <div class="font-medium text-gray-900 mb-1">Markdown lint · {{ promptNotes.length }} note{{ promptNotes.length === 1 ? '' : 's' }}<span class="ml-2 rounded bg-white border border-gray-200 px-2 text-[11px] font-normal text-gray-500">Beta · superusers only</span></div>
            <ul class="space-y-1">
              <li v-for="(n, i) in promptNotes" :key="i">
                <button type="button" class="text-left text-gray-700 hover:text-gray-900" @click="jumpToLine(n)">
                  <span class="font-mono text-gray-500">Line {{ n.line }}</span> · {{ n.message }}
                </button>
              </li>
            </ul>
          </div>
        </HermesCard>

        <HermesCard v-else-if="section === 'schema'" title="Result schema" :meta="`${fields.length} fields`">
          <template #actions>
            <div class="inline-flex rounded-md border border-gray-300 overflow-hidden">
              <button v-for="v in ['fields', 'json']" :key="v" type="button" :class="['px-3 py-1', schemaView === v ? 'bg-neutral-100 text-gray-900' : 'bg-white text-gray-600']" @click="schemaView = v">{{ v === 'json' ? 'Raw JSON' : 'Fields' }}</button>
            </div>
          </template>
          <template v-if="schemaView === 'json'">
            <textarea v-model="schemaText" rows="16" :class="[INPUT, 'font-mono']" spellcheck="false" />
            <p v-if="schemaError" class="mt-2 rounded bg-red-tint px-2 py-1 text-gray-900">{{ schemaError }}</p>
            <button type="button" :class="[TEXT_BUTTON, 'mt-2']" @click="schemaText = ''">Clear schema</button>
          </template>
          <template v-else>
            <p v-if="schemaError" class="text-gray-600">Fix the JSON to see fields.</p>
            <p v-else-if="!fields.length" class="text-gray-600">No schema: each result is free text.</p>
            <div v-else class="rounded-lg border border-gray-200 overflow-hidden">
              <table class="w-full table-fixed divide-y divide-gray-200">
                <colgroup><col style="width:30%"><col style="width:16%"><col style="width:10%"><col style="width:44%"></colgroup>
                <thead class="bg-gray-50"><tr><th :class="TH">Field</th><th :class="TH">Type</th><th :class="TH">Required</th><th :class="TH">Allowed</th></tr></thead>
                <tbody class="divide-y divide-gray-200">
                  <tr
                    v-for="f in visibleFields"
                    :key="f.path"
                    :class="f.children ? 'cursor-pointer hover:bg-gray-50' : ''"
                    :aria-expanded="f.children ? String(!!schemaOpen[f.path]) : null"
                    @click="f.children && toggleSchemaRow(f.path)"
                  >
                    <td class="px-3 py-2 align-top" :style="{ paddingLeft: `${12 + f.depth * 20}px` }">
                      <div class="flex items-start gap-2">
                        <font-awesome-icon v-if="f.children" :icon="['fas', schemaOpen[f.path] ? 'chevron-down' : 'chevron-right']" class="w-3 mt-0.5 text-gray-500" />
                        <span v-else class="w-3 shrink-0" />
                        <div class="min-w-0">
                          <span class="font-mono text-gray-900 break-words">{{ f.name }}</span>
                          <span v-if="f.children" class="ml-2 text-gray-500">{{ f.children }} field{{ f.children === 1 ? '' : 's' }}</span>
                          <div v-if="f.description" class="text-gray-500 mt-1">{{ f.description }}</div>
                        </div>
                      </div>
                    </td>
                    <td class="px-3 py-2 align-top text-gray-700">{{ f.type }}</td>
                    <td class="px-3 py-2 align-top" :class="f.required ? 'text-gray-900' : 'text-gray-500'">{{ f.required ? 'Required' : 'Optional' }}</td>
                    <td class="px-3 py-2 align-top">
                      <div v-if="f.enum.length || f.range" class="flex flex-wrap gap-1">
                        <span v-for="e in f.enum" :key="e" class="rounded bg-gray-100 px-2 font-mono text-gray-800">{{ e }}</span>
                        <span v-if="f.range" class="text-gray-700">{{ f.range }}</span>
                      </div>
                      <span v-else class="text-gray-500">—</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>
        </HermesCard>

        <HermesCard v-else-if="section === 'effects'" title="Effects">
          <p class="text-gray-700 mb-3">Writes this template may make. Each executor still needs a grant for each kind before a run starts.</p>
          <div v-for="area in areas" :key="area" class="mb-3">
            <h3 class="text-[11px] font-medium uppercase tracking-wider text-gray-500 mb-2">{{ area }}</h3>
            <div class="grid grid-cols-12 gap-2">
              <label v-for="k in kinds.filter((x) => x.area === area)" :key="k.kind" class="col-span-6 flex items-start gap-2">
                <input v-model="form.effects" type="checkbox" :value="k.kind" class="mt-1 rounded border-gray-300">
                <span><span class="text-gray-900">{{ k.label }}</span> <span class="text-gray-500">{{ k.kind }}</span>
                  <span class="block text-gray-600">{{ k.description }}</span></span>
              </label>
            </div>
          </div>
        </HermesCard>

        <HermesCard v-else-if="section === 'display'" title="Display">
          <template v-if="displayDocument">
            <div class="mb-3 flex items-center gap-3">
              <p class="text-gray-700">
                How this document looks, with placeholder text in each section.<span v-if="!isNew"> To change it, use <router-link :to="{ params: { ...$route.params, section: 'drafts' } }" :class="TEXT_BUTTON">Drafts</router-link>.</span>
              </p>
              <div class="ml-auto inline-flex flex-none overflow-hidden rounded-md border border-gray-200">
                <button v-for="v in DISPLAY_VIEWS" :key="v.key" type="button" :class="['px-3 py-1', displayView === v.key ? 'bg-neutral-100 text-gray-900' : 'bg-white text-gray-600']" :data-test="`display-view-${v.key}`" @click="displayView = v.key">{{ v.label }}</button>
              </div>
            </div>
            <div v-if="displayView === 'document'" class="rounded-md border border-gray-200 bg-gray-50 p-4" data-test="display-document">
              <SummaryDocumentPage :title="form.name || 'Document'" :sections="displayDocument.sections" :content="displayDocument.content" />
            </div>
          </template>
          <template v-else-if="displayReport">
            <div class="mb-3 flex items-center gap-3">
              <p class="text-gray-700">
                The sections of the {{ reportLabel.toLowerCase() }}, and what each one shows.<span v-if="!isNew"> To change it, use <router-link :to="{ params: { ...$route.params, section: 'drafts' } }" :class="TEXT_BUTTON">Edit with AI</router-link>.</span>
              </p>
              <div class="ml-auto inline-flex flex-none overflow-hidden rounded-md border border-gray-200">
                <button v-for="v in reportViews" :key="v.key" type="button" :class="['px-3 py-1', displayView === v.key ? 'bg-neutral-100 text-gray-900' : 'bg-white text-gray-600']" :data-test="`display-view-${v.key}`" @click="displayView = v.key">{{ v.label }}</button>
              </div>
            </div>
            <div v-if="displayView === 'document'" class="rounded-md border border-gray-200 bg-gray-50 p-4" data-test="display-report">
              <ReportLayout :display-schema="displayReport" outline />
            </div>
          </template>
          <template v-if="(!displayDocument && !displayReport) || displayView === 'json'">
          <p v-if="isQaRubric" class="text-gray-700 mb-2" data-test="qa-display-help">
            The rubric, under <span class="font-mono">qa</span>: <span class="font-mono">questions</span> (each with
            <span class="font-mono">id, question, section, points, scored, allow_na, auto_fail</span>),
            <span class="font-mono">rubric_key, rubric_label, team_type, task_type_ids, green_threshold, yellow_threshold</span>
            and an optional <span class="font-mono">disclaimer</span>. Saving checks it. Audits already submitted keep their answers.
          </p>
          <p v-else class="text-gray-700 mb-2">
            How results are shown. For a template with the Clinical document effect: <span class="font-mono">work_type</span> is the
            work type the document is saved as, <span class="font-mono">display_sections</span> lays it out on the Summary tab and
            <span class="font-mono">pdf_sections</span> on the PDF and the C-CDA sent to providers (screen layout if absent).
            Each section is <span class="font-mono">{"key": result field, "label": heading, "style": how it renders}</span>.
            Documents already generated keep the layout they were made with.
          </p>
          <textarea v-model="displayText" rows="20" :class="[INPUT, 'font-mono']" spellcheck="false" data-test="display-json" />
          <p v-if="parsedDisplay.error" class="mt-2 rounded bg-neutral-50 px-2 py-1 text-gray-900">{{ parsedDisplay.error }}</p>
          </template>
        </HermesCard>

        <HermesCard v-else-if="section === 'params'" title="Run parameters" meta="Derived; read-only">
          <p v-if="!params.length" class="text-gray-600">No run parameters. This template runs the same way every time.</p>
          <ul v-else class="space-y-3">
            <li v-for="p in params" :key="p.name">
              <span class="text-gray-900">{{ p.name }}</span>
              <span :class="['ml-2 rounded px-2', p.required ? 'bg-primary-100 text-gray-900' : 'bg-gray-100 text-gray-700']">{{ p.required ? 'Required' : 'Optional' }}</span>
              <span v-if="!savedParamNames.includes(p.name) && !isNew" class="ml-2 rounded bg-neutral-100 px-2 text-gray-900">New in draft</span>
              <div class="text-gray-500 mt-1">Used in {{ p.where.join(', ') }}</div>
            </li>
          </ul>
          <p class="text-gray-500 mt-4">Every run also accepts <span>sample</span> to score a fixed draw of units{{ form.population ? '' : '; this template needs a unit at start instead' }}.</p>
          <p v-if="derivedError" class="mt-3 rounded bg-neutral-50 px-2 py-1 text-gray-900">{{ derivedError }}</p>
        </HermesCard>

        <HermesTemplateDrafts v-else-if="section === 'drafts'" :template="saved" :versions="versions" @promoted="onPromoted" />

        <HermesCard v-else-if="section === 'history'" title="History">
          <ul class="space-y-3">
            <li v-for="(v, i) in versions" :key="v.history_id">
              <span class="font-medium">{{ v.version ? `Version ${v.version}` : 'Unnumbered version' }}</span>
              <span v-if="i === 0" class="ml-2 rounded bg-secondary-200 px-2 text-gray-900">Current</span>
              <div class="text-gray-500"><span :title="absolute(v.at)">{{ relative(v.at) }}</span><span v-if="v.by"> · <span class="sensitive">{{ v.by }}</span></span><span v-if="v.reason"> · {{ v.reason }}</span></div>
              <button v-if="versions[i + 1]" type="button" :class="TEXT_BUTTON" @click="diff = { a: versions[i + 1], b: v }">Diff vs {{ versionName(versions[i + 1]) }}</button>
            </li>
          </ul>
        </HermesCard>
      </div>
    </div>

    <div v-if="section !== 'drafts'" class="sticky bottom-0 z-10 -mx-8 border-t border-gray-200 bg-white px-8 py-3 flex items-center gap-4">
      <span v-if="changedSections.length" class="text-gray-600">Changed: {{ changedSections.join(', ') }}</span>
      <span v-if="savedNote" class="text-gray-600">{{ savedNote }}</span>
      <div v-if="saveError" role="alert" class="rounded-md bg-red-tint px-3 py-2 text-gray-900"><strong>Not saved.</strong> {{ saveError }}</div>
      <div class="ml-auto flex gap-3">
        <button v-if="!isNew" type="button" :class="TEXT_BUTTON" :disabled="!changedSections.length || saving" @click="reset">Discard</button>
        <button type="button" :class="PRIMARY_BUTTON" :disabled="saving || (!isNew && !changedSections.length)" @click="save">
          {{ saving ? 'Saving…' : (isNew ? 'Create template' : 'Save new version') }}
        </button>
      </div>
    </div>

    <div v-if="diff" class="fixed inset-0 z-20 flex items-center justify-center bg-gray-800/60" role="dialog" aria-modal="true">
      <div class="w-[900px] max-h-[80vh] flex flex-col rounded-lg bg-white border border-gray-200">
        <div class="bg-primary-100 px-4 py-3 rounded-t-lg flex items-center font-semibold text-gray-900">
          Version {{ versionName(diff.a) }} → {{ versionName(diff.b) }}
          <button type="button" class="ml-auto font-normal text-gray-700" @click="diff = null">Close</button>
        </div>
        <div class="overflow-y-auto p-4 space-y-4">
          <p v-if="!diffSections.length" class="text-gray-600">No change to the definition between these versions.</p>
          <div v-for="s in diffSections" :key="s.key">
            <h3 class="text-[11px] font-medium uppercase tracking-wider text-gray-500 mb-2">{{ s.key }}</h3>
            <pre class="whitespace-pre-wrap break-words"><span v-for="(l, i) in s.lines" :key="i" :class="['block px-2', l.op === '-' ? 'bg-gray-100 line-through text-gray-600' : (l.op === '+' ? 'bg-secondary-100 text-gray-900' : 'text-gray-700')]">{{ l.op }} {{ l.text }}</span></pre>
          </div>
        </div>
        <div class="px-4 py-3 border-t border-gray-200 flex justify-end">
          <button type="button" :class="SECONDARY_BUTTON" @click="restore(diff.a)">Restore version {{ versionName(diff.a) }} as a draft</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ENGAGEMENT_SUMMARY_KIND, QA_RUBRIC_KIND, TEAM_REVIEW_KIND, listEngagementTypes, listPrograms } from '@/services/api/hermesTemplates';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import MultiSelectDropdown from '@/components/widgetcomponents/library/MultiSelectDropdown.vue';
import PageSidebar from '@/components/widgetcomponents/library/PageSidebar.vue';
import FilterDropdown from '@/components/widgetcomponents/library/FilterDropdown.vue';
import Toggle from '@/components/widgetcomponents/Toggle.vue';
import tooltip from '@/directives/tooltip';
import HermesCard from './HermesCard.vue';
import HermesRecordHeader from './HermesRecordHeader.vue';
import HermesTemplateDrafts from './HermesTemplateDrafts.vue';
import ReportLayout from '@/components/PersonalAnalyst/ReportLayout.vue';
import SummaryDocumentPage from '@/components/Care_Management/Widgets/PatientEngagement/SummaryDocumentPage.vue';
import { sampleContent, sectionsFrom } from './documentSections';
import { lintPrompt } from './promptLint';
import { API, dataAttributeLocation, INPUT, PRIMARY_BUTTON, SECONDARY_BUTTON, TD, TEXT_BUTTON, TH, absolute, errorOf, reportSchema, lineDiff, relative, renderPrompt, words } from './hermes';

const DISPLAY_VIEWS = [{ key: 'document', label: 'Document' }, { key: 'json', label: 'JSON' }];
const DEFINITION = ['unit', 'population', 'inputs', 'prompt', 'result_schema', 'effects'];
const BLANK = { name: '', kind: '', description: '', is_active: true, unit: 'BENE_MBI_ID', population: null, inputs: [], prompt: '', result_schema: null, effects: [], display: {}, executor: null, metrics: [], engagement_types: [] };

export default {
  name: 'HermesTemplateEditor',
  components: { FilterDropdown, HermesCard, HermesRecordHeader, HermesTemplateDrafts, ReportLayout, LoadingIndicator, MultiSelectDropdown, PageSidebar, SummaryDocumentPage, Toggle },
  directives: { tooltip },
  data() {
    return {
      ENGAGEMENT_SUMMARY_KIND, TEAM_REVIEW_KIND, QA_RUBRIC_KIND, showCasePaste: false,
      promptView: 'write',
      casePaste: '',
      casePasteError: '',
      loading: true, loadError: '', saving: false, saveError: '', savedNote: '', saved: {}, form: JSON.parse(JSON.stringify(BLANK)),
      schemaText: '', displayText: '', displayView: 'document', DISPLAY_VIEWS, schemaView: 'fields', schemaOpen: {}, catalog: { data_attributes: [], sources: [], graph_nodes: [], default_graph_nodes: [] },
      kinds: [], areas: [], versions: [], schedules: [], params: [], derivedError: '', derivedTimer: null, diff: null, executors: [], programs: [], engagementTypes: [],
      INPUT, PRIMARY_BUTTON, SECONDARY_BUTTON, TD, TEXT_BUTTON, TH,
    };
  },
  computed: {
    // What this tenant calls a care management program (IHCI says Opportunities), as on the Patients page.
    programNoun() {
      return this.$store.getters['tenant/opportunityNounPlural'] || 'Programs';
    },
    programNounSingular() {
      return this.$store.getters['tenant/opportunityNoun'] || 'Program';
    },
    isSuperuser() {
      return !!this.$store.getters['auth/current_user']?.is_superuser;
    },
    promptNotes() {
      return lintPrompt(this.form.prompt);
    },
    promptHtml() {
      return renderPrompt(this.form.prompt);
    },
    singleBlocked() {
      return !this.isNew && !!this.saved.population && this.schedules.length > 0;
    },
    daHref() {
      return (id) => this.$router.resolve(dataAttributeLocation(id)).href;
    },
    schedulesHref() {
      return this.$router.resolve({ name: 'AdminHermesSchedules', query: { template: this.saved.id } }).href;
    },
    singleBlockedReason() {
      const n = this.schedules.length;
      return `Can't switch to Single run while this template has ${n} schedule${n === 1 ? '' : 's'}. Delete ${n === 1 ? 'it' : 'them'} on the Schedules tab first.`;
    },
    executorOptions() {
      return this.executors.filter((e) => e.usable || e.id === this.form.executor).map((e) => ({ value: e.id, label: `${e.name} (${words(e.runtime)})` }));
    },
    metricOptions() {
      const options = this.programs.map((m) => ({ value: m.id, label: m.name }));
      // A program saved before its metric stopped being a care management program: keep it visible so it can be removed.
      const listed = new Set(options.map((o) => o.value));
      const unlisted = (this.form.metrics || []).filter((id) => !listed.has(id))
        .map((id) => ({ value: id, label: `Metric ${id} (no longer a care management program)` }));
      return [...options, ...unlisted];
    },
    engagementTypeOptions() {
      const options = this.engagementTypes.map((t) => ({ value: t.name, label: t.display_name || t.name }));
      // An engagement type saved before its work type was retired: keep it visible so it can be removed.
      const listed = new Set(options.map((o) => o.value));
      const unlisted = (this.form.engagement_types || []).filter((name) => !listed.has(name))
        .map((name) => ({ value: name, label: `${name} (no longer an engagement type)` }));
      return [...options, ...unlisted];
    },
    dataAttributeOptions() {
      return this.catalog.data_attributes.map((d) => ({ value: d.id, label: `${d.name} (#${d.id})` }));
    },
    isNew() {
      return this.$route.params.templateId === 'new';
    },
    // Drafts (Edit with AI) are offered on templates with a kind: the kind tells the assistant what it is editing and
    // sets the checks its edits must pass.
    // On a QA rubric only for superusers: Edit with AI can change its prompt and result schema, but not the questions
    // in display.qa, so it's not offered to everyone yet.
    hasDrafts() {
      return !!this.saved?.kind && !!this.saved?.unit && (!this.isQaRubric || this.isSuperuser);
    },
    isQaRubric() {
      return this.saved?.kind === QA_RUBRIC_KIND;
    },
    // Programs only decide which engagements get a document template (its program button). Elsewhere they do nothing,
    // so they're shown only there, or while a template still has some, so they can be cleared.
    showsPrograms() {
      return this.saved?.kind === ENGAGEMENT_SUMMARY_KIND || this.form.kind === ENGAGEMENT_SUMMARY_KIND
        || this.isQaRubricForm || !!(this.form.metrics || []).length;
    },
    // A QA rubric's programs pick the calls it audits (their task types); it has no engagement types.
    isQaRubricForm() {
      return this.form.kind === QA_RUBRIC_KIND;
    },
    sections() {
      const count = (n) => (n ? String(n) : '');
      const all = [
        { id: 'overview', label: 'Overview' },
        { id: 'population', label: 'Population' },
        { id: 'inputs', label: 'Inputs', badge: count(this.form.inputs.length) },
        { id: 'prompt', label: 'Prompt' },
        { id: 'schema', label: 'Result schema', badge: count(this.fields.length) },
        { id: 'effects', label: 'Effects', badge: count(this.form.effects.length) },
        { id: 'display', label: 'Display' },
        { id: 'params', label: 'Run parameters', badge: count(this.params.length) },
        { id: 'drafts', label: 'Edit with AI ✨', badge: count(this.saved.drafts) },
        { id: 'history', label: 'History', badge: count(this.versions.length) },
      ];
      // Drafts are for documentation templates; a new template has no drafts or history yet.
      const hidden = this.isNew ? ['drafts', 'history'] : (this.hasDrafts ? [] : ['drafts']);
      return all.filter((x) => !hidden.includes(x.id));
    },
    section() {
      const wanted = this.$route.params.section;
      return this.sections.some((x) => x.id === wanted) ? wanted : 'overview';
    },
    sectionItems() {
      return this.sections.map((x) => ({ ...x, active: x.id === this.section, badgeClass: 'bg-gray-100 text-gray-700' }));
    },
    savedParamNames() {
      return (this.saved.params || []).map((p) => p.name);
    },
    parsedSchema() {
      if (!this.schemaText.trim()) return { value: null, error: '' };
      try {
        return { value: JSON.parse(this.schemaText), error: '' };
      } catch (e) {
        return { value: undefined, error: `The schema is not valid JSON: ${e.message}` };
      }
    },
    schemaError() {
      return this.parsedSchema.error;
    },
    // A document layout (display_sections) is shown as the document itself; the JSON stays one click away.
    displayDocument() {
      const display = this.parsedDisplay.value;
      const schema = this.parsedSchema.value;
      if (!display || !Array.isArray(display.display_sections) || !display.display_sections.length || this.parsedSchema.error) return null;
      return { sections: display.display_sections, content: sampleContent(sectionsFrom(schema, display)) };
    },
    // A team review's report is the patient summary shown on the Team Review board.
    reportLabel() {
      return (this.form.kind || this.saved?.kind) === TEAM_REVIEW_KIND ? 'Patient summary' : 'Report';
    },
    reportViews() {
      return [{ key: 'document', label: this.reportLabel }, { key: 'json', label: 'JSON' }];
    },
    displayReport() {
      return reportSchema(this.parsedDisplay.value);
    },
    parsedDisplay() {
      if (!this.displayText.trim()) return { value: {}, error: '' };
      try {
        const value = JSON.parse(this.displayText);
        if (!value || Array.isArray(value) || typeof value !== 'object') return { value: undefined, error: 'Display must be a JSON object.' };
        return { value, error: '' };
      } catch (e) {
        return { value: undefined, error: `Display is not valid JSON: ${e.message}` };
      }
    },
    fields() {
      const s = this.parsedSchema.value;
      return s && s.properties ? Object.keys(s.properties) : [];
    },
    flatFields() {
      const out = [];
      const walk = (schema, depth, prefix, ancestors) => {
        const req = new Set(schema.required || []);
        Object.entries(schema.properties || {}).forEach(([name, spec]) => {
          const s = spec || {};
          const type = s.type === 'array' ? `list of ${(s.items || {}).type || 'value'}` : `${s.type || 'value'}${s.enum ? ' · enum' : ''}`;
          const range = s.minimum !== undefined || s.maximum !== undefined ? `${s.minimum ?? ''}–${s.maximum ?? ''}` : '';
          const child = s.type === 'object' ? s : (s.type === 'array' && (s.items || {}).type === 'object' ? s.items : null);
          const path = `${prefix}${name}`;
          out.push({ path, name, depth, ancestors, type, required: req.has(name), enum: s.enum || [], range, description: s.description || '', children: child ? Object.keys(child.properties || {}).length : 0 });
          if (child) walk(child, depth + 1, `${path}${s.type === 'array' ? '[]' : ''}.`, [...ancestors, path]);
        });
      };
      if (this.parsedSchema.value) walk(this.parsedSchema.value, 0, '', []);
      return out;
    },
    visibleFields() {
      return this.flatFields.filter((f) => f.ancestors.every((a) => this.schemaOpen[a]));
    },
    payload() {
      return {
        ...this.form,
        result_schema: this.parsedSchema.value === undefined ? this.form.result_schema : this.parsedSchema.value,
        display: this.parsedDisplay.value === undefined ? this.form.display : this.parsedDisplay.value,
      };
    },
    changedSections() {
      if (this.isNew) return [];
      const labels = { name: 'name', kind: 'type', description: 'description', is_active: 'active', unit: 'unit', population: 'population', inputs: 'inputs', prompt: 'prompt', result_schema: 'result schema', effects: 'effects', display: 'display', executor: 'executor', metrics: 'programs', engagement_types: 'engagement types' };
      return Object.keys(labels).filter((k) => JSON.stringify(this.payload[k] ?? null) !== JSON.stringify(this.saved[k] ?? null)).map((k) => labels[k]);
    },
    diffSections() {
      if (!this.diff) return [];
      const a = { name: this.diff.a.name, description: this.diff.a.description, ...this.diff.a.definition, display: this.diff.a.display };
      const b = { name: this.diff.b.name, description: this.diff.b.description, ...this.diff.b.definition, display: this.diff.b.display };
      return ['name', 'description', ...DEFINITION, 'display'].filter((k) => JSON.stringify(a[k]) !== JSON.stringify(b[k])).map((k) => ({
        key: k, lines: lineDiff(typeof a[k] === 'string' ? a[k] : JSON.stringify(a[k], null, 2), typeof b[k] === 'string' ? b[k] : JSON.stringify(b[k], null, 2)),
      }));
    },
  },
  watch: {
    form: { deep: true, handler: 'derive' },
    schemaText: 'derive',
  },
  mounted() {
    this.load();
  },
  methods: {
    absolute, relative, words,
    async load() {
      try {
        const calls = [this.$axios.get(`${API}/templates/catalog/`), this.$axios.get(`${API}/write-kinds/`), this.$axios.get(`${API}/executors/`), listPrograms(), listEngagementTypes()];
        if (!this.isNew) {
          const id = this.$route.params.templateId;
          calls.push(this.$axios.get(`${API}/templates/${id}/`), this.$axios.get(`${API}/templates/${id}/versions/`), this.$axios.get(`${API}/schedules/`));
        }
        const [catalog, kinds, executors, programs, engagementTypes, template, versions, schedules] = await Promise.all(calls);
        this.catalog = catalog.data;
        this.executors = executors.data;
        this.programs = programs;
        this.engagementTypes = engagementTypes;
        this.kinds = kinds.data.kinds;
        this.areas = kinds.data.areas;
        if (!this.isNew) {
          this.saved = template.data;
          this.versions = versions.data;
          this.schedules = schedules.data.filter((sc) => sc.template === template.data.id);
        }
        this.reset();
      } catch (e) {
        this.loadError = e?.response?.status === 404 ? 'No template with that id on this tenant.' : errorOf(e, 'The template could not be loaded.');
      } finally {
        this.loading = false;
      }
    },
    versionName(v) {
      return v.version ?? 'unnumbered';
    },
    async onPromoted(template) {
      this.saved = template;
      this.versions = (await this.$axios.get(`${API}/templates/${template.id}/versions/`)).data;
      this.reset();
      this.savedNote = `Promoted to live as version ${template.version}.`;
    },
    reset() {
      const src = this.isNew ? BLANK : this.saved;
      this.form = JSON.parse(JSON.stringify(Object.fromEntries(Object.keys(BLANK).map((k) => [k, src[k] ?? BLANK[k]]))));
      this.schemaText = this.form.result_schema ? JSON.stringify(this.form.result_schema, null, 2) : '';
      this.displayText = Object.keys(this.form.display || {}).length ? JSON.stringify(this.form.display, null, 2) : '';
      this.params = this.saved.params || [];
      this.saveError = '';
    },
    derive() {
      clearTimeout(this.derivedTimer);
      this.derivedTimer = setTimeout(async () => {
        try {
          const { data } = await this.$axios.post(`${API}/templates/params/`, this.payload);
          this.params = data.params;
          this.derivedError = data.error || '';
        } catch {
          this.derivedError = '';
        }
      }, 250);
    },
    toggleSchemaRow(path) {
      this.schemaOpen = { ...this.schemaOpen, [path]: !this.schemaOpen[path] };
    },
    openSection(id) {
      if (id === this.section) return;
      const { draftId, ...params } = this.$route.params; // eslint-disable-line no-unused-vars
      this.$router.push({ params: { ...params, section: id === 'overview' ? '' : id } });
    },
    move(i, dir) {
      const [item] = this.form.inputs.splice(i, 1);
      this.form.inputs.splice(i + dir, 0, item);
    },
    addInput(kind) {
      const base = { kind, label: `${kind}_${this.form.inputs.length + 1}`, description: '' };
      if (kind === 'data_attribute') base.ref = null;
      if (kind === 'patient_graph') Object.assign(base, { label: 'patient_record', nodes: null, required: true });
      if (kind === 'source') base.ref = (this.catalog.sources[0] || {}).name || '';
      if (kind === 'case') Object.assign(base, { turns: [], answers: [], hats: '', expect: {}, rubric: '' });
      this.form.inputs.push(base);
    },
    lines(text) {
      return String(text || '').split('\n').map((s) => s.trim()).filter(Boolean);
    },
    expectText(item) {
      return Object.keys(item.expect || {}).length ? JSON.stringify(item.expect) : '';
    },
    setExpect(item, text) {
      if (!text.trim()) { item.expect = {}; delete item._expectError; return; }
      try {
        const parsed = JSON.parse(text);
        if (!parsed || Array.isArray(parsed) || typeof parsed !== 'object') throw new Error('must be an object');
        item.expect = parsed;
        delete item._expectError;
      } catch (e) {
        item._expectError = `Not valid JSON: ${e.message}`;
      }
    },
    importCases() {
      this.casePasteError = '';
      let list;
      try {
        list = JSON.parse(this.casePaste);
      } catch (e) {
        this.casePasteError = `Not valid JSON: ${e.message}`;
        return;
      }
      if (!Array.isArray(list) || !list.length) { this.casePasteError = 'Expected a non-empty JSON list.'; return; }
      const cases = list.map((c, i) => ({
        kind: 'case', label: String(c.label || `case ${i + 1}`), description: String(c.description || ''),
        turns: Array.isArray(c.turns) ? c.turns.map(String) : [], answers: Array.isArray(c.answers) ? c.answers.map(String) : [],
        hats: String(c.hats || ''), expect: (c.expect && typeof c.expect === 'object') ? c.expect : {}, rubric: String(c.rubric || ''),
      }));
      this.form.inputs = this.form.inputs.filter((x) => x.kind !== 'case').concat(cases);
      this.showCasePaste = false;
      this.casePaste = '';
    },
    setRender(item, value) {
      if (value) item.render = value;
      else delete item.render;
    },
    filterRows(item) {
      return Object.entries(item.filters || {}).map(([name, value]) => ({ name, value }));
    },
    writeFilters(item, rows) {
      const next = Object.fromEntries(rows.filter((r) => r.name !== undefined).map((r) => [r.name, r.value]));
      if (Object.keys(next).length) item.filters = next;
      else delete item.filters;
    },
    setFilter(item, j, key, value) {
      const rows = this.filterRows(item);
      rows[j] = { ...rows[j], [key]: value };
      this.writeFilters(item, rows);
    },
    addFilter(item) {
      this.writeFilters(item, [...this.filterRows(item), { name: `filter_${this.filterRows(item).length + 1}`, value: '' }]);
    },
    removeFilter(item, j) {
      const rows = this.filterRows(item);
      rows.splice(j, 1);
      this.writeFilters(item, rows);
    },
    toggleNode(item, key) {
      const current = item.nodes || [...this.catalog.default_graph_nodes];
      item.nodes = current.includes(key) ? current.filter((k) => k !== key) : [...current, key];
    },
    restore(version) {
      Object.assign(this.form, JSON.parse(JSON.stringify({
        name: version.name, description: version.description, ...version.definition, display: version.display || {},
      })));
      this.schemaText = version.definition.result_schema ? JSON.stringify(version.definition.result_schema, null, 2) : '';
      this.displayText = Object.keys(version.display || {}).length ? JSON.stringify(version.display, null, 2) : '';
      this.diff = null;
    },
    async jumpToLine(note) {
      this.promptView = 'write';
      await this.$nextTick();
      const box = this.$refs.prompt;
      box.focus();
      box.setSelectionRange(note.offset, note.offset + note.length);
      box.scrollTop = Math.max(0, (note.line - 3) * parseFloat(getComputedStyle(box).lineHeight || '20'));
    },
    async save() {
      if (this.schemaError || this.parsedDisplay.error) {
        this.saveError = this.schemaError || this.parsedDisplay.error;
        return;
      }
      if (this.singleBlocked && !this.form.population) {
        this.saveError = this.singleBlockedReason;
        return;
      }
      this.saving = true;
      this.saveError = '';
      try {
        if (this.isNew) {
          const { data } = await this.$axios.post(`${API}/templates/`, this.payload);
          this.form = JSON.parse(JSON.stringify(BLANK));
          this.$router.replace({ name: 'AdminHermesTemplate', params: { templateId: data.id } });
          return;
        }
        const { data } = await this.$axios.patch(`${API}/templates/${this.saved.id}/`, this.payload);
        this.saved = data;
        this.versions = (await this.$axios.get(`${API}/templates/${data.id}/versions/`)).data;
        this.reset();
        this.savedNote = `Saved as version ${data.version}.`;
        setTimeout(() => { this.savedNote = ''; }, 4000);
      } catch (e) {
        this.saveError = errorOf(e, 'The platform did not accept the template.');
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
